import $ from 'jquery';
import { each, times } from 'lodash';
import * as Sentry from '@sentry/browser';

// Set correct colspan on swimlanes. While the existing hardcoded value of "2000"
// seemingly works, table layouts are particular and fragile things, and nonsensical
// values like this can cause unexpected rendering issues that are impossible to
// track down or fix (IE11 for example breaks when colspan is set to a number that
// is larger than a possible value).
const setSwimlaneColumns = function ($table) {
  let totalCols = 0;
  $table
    .find('> tbody > tr:not(.pivot-swimlane)')
    .first()
    .children()
    .each(function () {
      return (totalCols = totalCols + this.colSpan);
    });

  const $swimlanes = $table.find('tbody .pivot-swimlane');
  return $swimlanes.each(function () {
    return $(this).find('> *').attr('colspan', totalCols);
  });
};

// Build up a table-based view for printing and web display.
// Flattens all nested tables to a single table with correct rowspan/colspan attributes on cells.
export default function ($elem) {
  let hasCornerPivot;
  if ($elem.find('.headings-table').length === 0) {
    setSwimlaneColumns($elem);
    return;
  } else {
    let $tableRows = $elem.find('> thead > tr');
    const numHeaderRows = $tableRows.length;
    const $cornerTable = $elem.find('> thead .empty-heading');
    const $cornerRows = $cornerTable.find('tr');
    const numCornerRows = $cornerRows.length;
    hasCornerPivot = $elem.find('.corner-color').length > 0;

    if (numHeaderRows > numCornerRows) {
      // Ensure that cells IN corner have proper rowSpan.
      const cornerRowSpan = $cornerTable[0].rowSpan;
      const remainder = cornerRowSpan - numCornerRows;
      $cornerTable
        .find('tr')
        .last()
        .children()
        .each(function () {
          return (this.rowSpan = this.rowSpan + remainder);
        });
    } else if (numHeaderRows < numCornerRows) {
      // Ensure that cells NOT IN corner have proper rowSpan.
      let difference = numCornerRows - $tableRows.length;
      while (difference > 0) {
        $tableRows.parent().append($('<tr></tr>'));
        difference = difference - 1;
      }
      $elem
        .find('> thead > tr:nth-last-child(2)')
        .children()
        .each(function () {
          return (this.rowSpan = 2);
        });
    }

    // Move cells in corner table out to the main table. Make the cell into a <TH> as that is the appropriate tag.
    $tableRows = $elem.find('> thead > tr');
    $cornerRows.each(function (cornerRowIndex) {
      const row = this;
      const children = Array.from(row.children).reverse();
      return children.forEach(function (cell, cellIndex) {
        const th = document.createElement('th');
        th.rowSpan = cell.rowSpan;
        th.colSpan = cell.colSpan;
        // this is safe since we are moving HTML from one part of the DOM to another.
        // eslint-disable-next-line no-unsanitized/property
        th.innerHTML = cell.innerHTML;
        // pass on any attributes that have been set in ruby on the td
        th.id = cell.id;
        $(th).attr('style', $(cell).attr('style'));
        $(th).data('width', $(cell).data('width'));

        for (let cl of cell.classList.values()) {
          th.classList.add(cl);
        }

        if (
          th.innerHTML.length === 0 &&
          !cell.classList.contains('corner-color')
        ) {
          th.classList.add('empty-cell');
        } else {
          th.classList.add('pivot-cell');
        }
        if (cellIndex === 0) {
          th.classList.add('last');
        }
        return $tableRows.eq(cornerRowIndex).prepend(th);
      });
    });

    // Delete corner table.
    $cornerTable.remove();
  }

  // Add specific class for any cell that spans multiple rows.
  $elem.find('th, td').each(function () {
    if (this.rowSpan > 1) {
      return this.classList.add('spansRow');
    }
  });

  $elem.find('tbody .pivot-table-row:not(.pivot-swimlane)').each(function () {
    const $row = $(this);
    // Set correct colspan on left column headers.
    if (hasCornerPivot) {
      $row.find('> th.last').attr('colspan', 2);
      $row
        .find('> .empty-heading.total')
        .last()
        .attr('colspan', 2)
        .addClass('last');
    }

    // Fix PhantomJS issue that causes <img> tags which are the sole child of <p> tags
    // to be displayed with massive amounts of white space after them. We check to see
    // if the image is the last child node (not child element) and if it is, force it
    // to render as a '-webkit-box' element. This prevents the issue while at the same
    // time allowing text to appear next to images if necessary.
    // We use the archaic '-webkit-box' notation (rather than the more modern
    // '-webkit-flex' or 'flex' notations) so as to specifically target PhantomJS.
    return $row.find('td .user-content p').each(function () {
      if (
        this.childNodes.length === 1 &&
        this.childNodes[0].tagName === 'IMG'
      ) {
        return (this.style.display = '-webkit-box');
      }
    });
  });

  return setSwimlaneColumns($elem);
}

export const alignPivotTableCells = function ($elem) {
  if (!$elem.hasClass('has-cell-headers')) {
    return;
  }
  const $tableRows = $elem.find('tr.pivot-table-row');

  try {
    // Iterate vertically on all top level rows (pivot-table-row)
    each($tableRows, function (pivotTableRow) {
      let $tableCells = $(pivotTableRow).find('td.pivot-table-cell');

      let cellsPerRowCount = $tableCells.length;
      let cellsPerColumnCount = Math.max.apply(
        null,
        $tableCells.map((i, e) => $(e).find('div.cell-row').length)
      );

      // Iterate vertically on all the rows (cell-row)
      times(cellsPerColumnCount, function (rowIndex) {
        let maxCellHeight = 0;
        let cells = [];

        // Iterate horizontally on all the columns (pivot-table-cell)
        times(cellsPerRowCount, function (cellIndex) {
          let $cell = $(
            $($tableCells[cellIndex]).find('div.cell-row')[rowIndex]
          );

          // Reset the height to make sure we get the acutal content height -- helps with bringing the height back down
          $cell.css({ minHeight: 0 });

          // Find the max cell height across all columns
          if ($cell.outerHeight(true) > maxCellHeight) {
            maxCellHeight = $cell.outerHeight(true);
          }

          // Add to a temp array to update all at once
          cells.push($cell);
        });

        // Set the new height for all
        each(cells, function (cell) {
          cell.css({ minHeight: maxCellHeight });
        });
      });
    });
  } catch (error) {
    Sentry.captureException(error);
  }
};
